import {
  EmptyObject,
  type TRecordType,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { memo, ReactNode, useMemo } from 'react';
import styles from './CardTable.module.scss';
import { RecordCard } from './RecordCard';
import {
  MAX_COLUMNS_PER_ROW,
  type CardColumn,
  type CardHeader,
  type TColumnsPerRow,
} from './type';
import { VirtualList } from './VirtualList';

const cx = classnames.bind(styles);

/**
 * Initial per-card height guess (px) before each card measures itself. Only
 * affects the scrollbar thumb on first paint — the card's real height is
 * measured right after mount, so an approximate value is fine.
 */
const ESTIMATED_CARD_HEIGHT = 320;
/**
 * Extra cards rendered above/below the viewport so fast scrolling doesn't flash
 * blank space before the next window mounts.
 */
const CARD_OVERSCAN = 6;

export type Props<RecordType extends TRecordType> = {
  /** Accepts the same column definitions as `Table`. */
  columns: CardColumn<RecordType>[];
  data: RecordType[] | undefined;
  /**
   * Number of columns per card row. The consumer decides this from its own
   * layout (e.g. a breakpoint or a container width it already tracks) —
   * CardTable does not measure its own width, since a fixed multi-panel
   * layout can look "stuck" even when the measurement is correct (the box
   * genuinely isn't resizing).
   * @default 6
   */
  columnsPerRow?: TColumnsPerRow;
  /**
   * Header row at the top of each card. Omit to render no header.
   *
   * Wrap this in `useCallback` for long lists — each card is memoized, and an
   * inline function is a new identity on every render, which defeats the memo.
   */
  header?: (record: RecordType, index: number, total: number) => CardHeader;
  /**
   * Pins each card's header to the top of the scroll container while that card
   * is in view. To offset the pinned position (e.g. another fixed element sits
   * above the scroll area), set the `--card-table-sticky-top` CSS variable on
   * the consumer side.
   */
  stickyHeader?: boolean;
  /**
   * Message shown under the illustration when there is no data.
   * Use 「オブジェクト名はありません」 when filtering and genuinely-empty
   * states coexist, 「追加されたオブジェクト名はありません」 for a list with
   * no filtering.
   */
  emptyMessage?: ReactNode;
  /**
   * Action placed below the card list (e.g. 「残りをすべて表示」).
   * The label and behaviour are app concerns, so the button is passed in.
   */
  footer?: ReactNode;
  /**
   * Window against the nearest scrollable ancestor instead of an internal
   * bounded viewport, so the cards grow with — and scroll with — the
   * surrounding page (single scrollbar, like the table layout). Only the
   * visible window of cards is mounted either way.
   */
  windowScroll?: boolean;
  testId?: string;
};

/** Illustration size from the design (smaller than EmptyObject's default). */
const EMPTY_ILLUST_WIDTH = 56;
const EMPTY_ILLUST_HEIGHT = 62;

const _CardTable = <RecordType extends TRecordType>({
  columns,
  data,
  columnsPerRow = MAX_COLUMNS_PER_ROW,
  header,
  stickyHeader,
  emptyMessage,
  footer,
  windowScroll,
  testId,
}: Props<RecordType>) => {
  const visibleColumns = useMemo(
    () => columns.filter((column) => !column.hideInCard),
    [columns]
  );

  const records = data ?? [];

  if (records.length === 0) {
    return (
      <div className={cx('root')} data-testid={testId}>
        <div className={cx('empty')}>
          <EmptyObject
            width={EMPTY_ILLUST_WIDTH}
            height={EMPTY_ILLUST_HEIGHT}
          />
          {emptyMessage && (
            <div className={cx('empty-message')}>{emptyMessage}</div>
          )}
        </div>
        {footer && <div className={cx('footer')}>{footer}</div>}
      </div>
    );
  }

  const cardTestId = testId ? `${testId}-card` : undefined;

  return (
    <div className={cx('root')} data-testid={testId}>
      <VirtualList<RecordType>
        items={records}
        getItemKey={(index) => records[index]?.id ?? index}
        estimateSize={ESTIMATED_CARD_HEIGHT}
        overscan={CARD_OVERSCAN}
        viewportClassName={cx('viewport')}
        listClassName={cx('list')}
        windowScroll={windowScroll}
      >
        {(record, { index, start, measureRef, key }) => (
          <RecordCard<RecordType>
            key={key}
            columns={visibleColumns}
            record={record}
            index={index}
            total={records.length}
            columnsPerRow={columnsPerRow}
            header={header}
            stickyHeader={stickyHeader}
            testId={cardTestId}
            start={start}
            virtualIndex={index}
            measureRef={measureRef}
          />
        )}
      </VirtualList>
      {footer && <div className={cx('footer')}>{footer}</div>}
    </div>
  );
};
_CardTable.displayName = '_CardTable';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const CardTable = memo((props) => (
  <_CardTable {...props} />
)) as typeof _CardTable;
CardTable.displayName = 'CardTable';
